/* eslint-disable jsx-a11y/click-events-have-key-events */
import { IconCalendarMonth, IconSelector } from "@tabler/icons-react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import React, { useMemo } from "react";
import InputMask from "react-input-mask";

import AnchoredDiv from "../AnchoredDiv/AnchoredDiv";

import DateSelector from "./DateSelector";
import { DateSelectorView } from "./DateSelector/DateSelector";
import { DatePickerType } from "./shared";

dayjs.extend(customParseFormat);
dayjs.extend(utc);

export type DatePickerProps = {
  /** See {@link DateSelector} for information on these views */
  view: DateSelectorView | `${DateSelectorView}`;
  type: DatePickerType | `${DatePickerType}`;
  name: string;

  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;

  dateFormatter?: (date?: Date, view?: DateSelectorView | `${DateSelectorView}`) => string;
  dateMask?: (view?: DateSelectorView | `${DateSelectorView}`) => {
    /** The mask to use for react-input-mask */
    mask: string;
    /** Format to use when parsing with dayjs */
    format: string;
  };

  /** Allow free text input and send onChange once finished, and validate onBlur. */
  allowFree?: boolean;
  /** Preserve the hour of the original value (can be used to combine with a timepicker) */
  preserveTime?: boolean;
  autoClose?: boolean;
  onChange?: (value: Date | [Date, Date]) => void;
} & ({
  type: DatePickerType.single | `${DatePickerType.single}`;
  value?: Date;
  onChange?: (value: Date) => void;
  okText?: never;
  cancelText?: never;
} | {
  type: DatePickerType.range | `${DatePickerType.range}`;
  value?: [Date, Date];
  onChange?: (value: [Date, Date]) => void;
  okText?: string;
  cancelText?: string;
})

const styles = {
  active: "border rounded-lg border-border-strong hover:bg-bg-components-hover hover:cursor-pointer focus-within:border-border-interactive-base focus-within:outline-focus active:outline-input-interactive active::border-border-interactive-base",
  disabled: "border rounded-lg border-border-disabled select-none bg-bg-disabled text-text-disabled",
};

const iconStyles = {
  active: "text-icon-bg-subtle",
  disabled: "text-icon-disabled",
};

const defaultDateFormatter = (date?: Date, view?: DateSelectorView) => {
  switch (view) {
    case DateSelectorView.day:
      if (!date) return "dd.mm.yyyy";
      return dayjs(date).format("DD.MM.YYYY");
    case DateSelectorView.month:
      if (!date) return "mm.yyyy";
      return dayjs(date).format("MM.YYYY");
    case DateSelectorView.year:
      if (!date) return "yyyy";
      return dayjs(date).format("YYYY");
    default:
      return "dd.mm.yyyy";
  }
};

const defaultDateMask = (view?: DateSelectorView) => {
  switch (view) {
    case DateSelectorView.year:
      return {
        mask: "9999",
        format: "YYYY",
      };
    case DateSelectorView.month:
      return {
        mask: "99.9999",
        format: "MM.YYYY",
      };
    case DateSelectorView.day:
    default:
      return {
        mask: "99.99.9999",
        format: "DD.MM.YYYY",
      };
  }
};

// TODO: See if can be refactored to use SDS Input component when it is created.
/**
 * Date picker component.
 * This component exclusively uses UTC time.
 *
 * This will not automatically co-operate with Formik due its complex nature. You
 * are responsible for handling value, onChange, disabled and error props.
 */
const DatePicker: React.FC<DatePickerProps> = ({
  view = DateSelectorView.day,
  type,
  name,
  value,
  onChange,
  disabled,
  minDate,
  maxDate,
  allowFree = false,
  preserveTime = false,
  autoClose = true,
  dateFormatter = defaultDateFormatter,
  dateMask = defaultDateMask,
  okText,
  cancelText,
}) => {
  const [show, setShow] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [internalValue, setInternalValue] = React.useState<string | [string, string]>();
  const variant = disabled ? "disabled" : "active";
  const isTypingRef = React.useRef(false);

  const handleChange = (newValue: Date | [Date, Date]) => {
    if (preserveTime) {
      if (Array.isArray(newValue) && Array.isArray(value)) {
        const [start, end] = (value as [Date, Date]);
        if (start) newValue[0].setHours(start.getHours(), start.getMinutes(), start.getSeconds(), start.getMilliseconds());
        if (end) newValue[1].setHours(end.getHours(), end.getMinutes(), end.getSeconds(), end.getMilliseconds());
      } else if (newValue instanceof Date && value instanceof Date) {
        const start = value as Date;
        if (start) newValue.setHours(start.getHours(), start.getMinutes(), start.getSeconds(), start.getMilliseconds());
      }
    }

    onChange(newValue);
    if (autoClose) setShow(false);
  };

  const validateDate = (date: dayjs.Dayjs) => {
    if (minDate && date.isBefore(dayjs.utc(minDate))) return false;
    if (maxDate && date.isAfter(dayjs.utc(maxDate))) return false;
    return true;
  };

  const mask = useMemo(() => dateMask(view as DateSelectorView), [view]);

  return (
    <>
      <div
        className={`flex flex-row p-2 space-x-3 typography-medium-base items-center ${styles[variant]}`}
        ref={anchorRef}
        onClick={() => {
          if (!disabled) {
            setShow(!show);
          }
        }}
      >
        <span className={iconStyles[variant]}>
          <IconCalendarMonth />
        </span>
        {allowFree && !disabled ? (
          <InputMask
            mask={
              type === "single"
                ? mask.mask
                : `${mask.mask} - ${mask.mask}`
            }
            maskChar=""
            placeholder={
              type === "single"
                ? dateFormatter(undefined, view)
                : `${dateFormatter(undefined, view)} - ${dateFormatter(undefined, view)}`
            }
            className={`flex-1 focus:outline-hidden bg-transparent ${!allowFree && !disabled ? "hover:cursor-pointer" : ""}`}
            disabled={disabled || !allowFree}
            onClick={(e) => {
              if (allowFree) e.stopPropagation();
            }}
            onChange={(e) => {
              // Allow free modifications until blur-sm when typing
              isTypingRef.current = true;
              setInternalValue(e.target.value);
            }}
            onBlur={(e) => {
              // We've stopped typing, validate and set
              isTypingRef.current = false;
              if (allowFree) {
                if (type === "single") {
                  const potentialDate = dayjs(e.target.value, mask.format);
                  if (potentialDate.isValid() && validateDate(potentialDate)) onChange(potentialDate.toDate());
                } else {
                  const [start, end] = e.target.value.split(" - ");
                  const potentialStart = dayjs(start, mask.format);
                  const potentialEnd = dayjs(end, mask.format);
                  if (potentialStart.isValid()
                    && potentialEnd.isValid()
                    && validateDate(potentialStart)
                    && validateDate(potentialEnd)
                    && potentialEnd.isAfter(potentialStart)
                  ) {
                    onChange([potentialStart.toDate(), potentialEnd.toDate()]);
                  }
                }

                setInternalValue(undefined);
              }
            }}
            value={
              (() => {
                // If typing, don't validate/format
                if (isTypingRef.current) {
                  return internalValue;
                }

                if (type === "single") {
                  return dateFormatter(value, view);
                }

                return `${dateFormatter(value?.[0], view)} - ${dateFormatter(value?.[1], view)}`;
              })()
            }
          />
        ) : (
          <span
            className="flex-1 focus:outline-hidden bg-transparent text-text-subtle"
          >
            {type === "single" ? dateFormatter(value, view) : `${dateFormatter(value?.[0], view)} - ${dateFormatter(value?.[1], view)}`}
          </span>
        )}
        <span className={iconStyles[variant]}>
          <IconSelector />
        </span>
      </div>
      <AnchoredDiv anchorRef={anchorRef} show={show} onBlur={() => setShow(false)} unmountOnBlur>
        {/* @ts-expect-error variants */}
        <DateSelector
          view={view}
          type={type}
          minDate={minDate}
          maxDate={maxDate}
          value={value}
          onChange={handleChange}
          onCancel={() => setShow(false)}
          defaultDate={Array.isArray(value) ? value[0] : value || new Date()}
          okText={okText}
          cancelText={cancelText}
        />
      </AnchoredDiv>
    </>
  );
};

export default DatePicker;
